// src/hooks/usePortfolioState.js
import { useState, useEffect } from 'react';
import { 
  getPortfolioTargets, 
  savePortfolioTargets, 
  updateCategory,
  deleteCategory 
} from '../services/firebase/portfolioService';

export function usePortfolioState(userEmail, selectedPortfolioId = 'default') {
  const [portfolioData, setPortfolioData] = useState({});
  const [loading, setLoading] = useState(true);
  const [modalState, setModalState] = useState({
    showAddAsset: false,
    showAddCategory: false,
    selectedParentPath: null
  });

  useEffect(() => {
    // Portföy değiştiğinde verileri sıfırla
    setPortfolioData({});
    
    const loadData = async () => {
      if (!userEmail || !selectedPortfolioId) {
        console.warn('No email or portfolio ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('Loading portfolio data for:', userEmail);
        const data = await getPortfolioTargets(userEmail, selectedPortfolioId);
        setPortfolioData(data);
      } catch (error) {
        console.error('Error loading portfolio data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [userEmail, selectedPortfolioId]);

  const handleAddAsset = (parentPath) => {
    setModalState({
      ...modalState,
      showAddAsset: true,
      selectedParentPath: parentPath
    });
  };

  const handleAssetSubmit = async (assetData) => {
    try {
      // 1. Derin kopya oluştur
      const updatedData = JSON.parse(JSON.stringify(portfolioData));
      const affectedCategories = new Set();
      const symbolToRemove = assetData.symbol;

      // 2. Mevcut varlığı diğer yerlerden sil
      Object.keys(updatedData).forEach(catKey => {
        let categoryModified = false;
        const category = updatedData[catKey];

        // Root assets kontrol et
        if (category.assets && category.assets[symbolToRemove]) {
            delete category.assets[symbolToRemove];
            categoryModified = true;
        }

        // Subcategories kontrol et
        if (category.subCategories) {
            Object.keys(category.subCategories).forEach(subKey => {
                const subCat = category.subCategories[subKey];
                if (subCat.assets && subCat.assets[symbolToRemove]) {
                    delete subCat.assets[symbolToRemove];
                    categoryModified = true;
                }
            });
        }

        if (categoryModified) {
            affectedCategories.add(catKey);
        }
      });

      // 3. Yeni konuma ekle
      const pathParts = modalState.selectedParentPath ? modalState.selectedParentPath.split('|') : [];
      const mainCategory = pathParts[0];
      const isSubCategory = pathParts.includes('subCategories');
      const subCategoryName = isSubCategory ? pathParts[2] : null;

      const assetEntry = {
        symbol: assetData.symbol,
        allocation: parseFloat(assetData.allocation) || 0,
        ...(assetData.description && { description: assetData.description })
      };

      if (isSubCategory && subCategoryName) {
        if (!updatedData[mainCategory]) {
          updatedData[mainCategory] = {
            name: mainCategory,
            allocation: 0,
            subCategories: {}
          };
        }

        if (!updatedData[mainCategory].subCategories) {
          updatedData[mainCategory].subCategories = {};
        }

        if (!updatedData[mainCategory].subCategories[subCategoryName]) {
          updatedData[mainCategory].subCategories[subCategoryName] = {
            name: subCategoryName,
            allocation: 0,
            assets: {}
          };
        }

        if (!updatedData[mainCategory].subCategories[subCategoryName].assets) {
          updatedData[mainCategory].subCategories[subCategoryName].assets = {};
        }

        updatedData[mainCategory].subCategories[subCategoryName].assets[assetData.symbol] = assetEntry;
      } else {
        const category = mainCategory || assetData.type;
        if (!updatedData[category]) {
          updatedData[category] = {
            name: category,
            allocation: 0,
            assets: {}
          };
        }

        if (!updatedData[category].assets) {
          updatedData[category].assets = {};
        }

        updatedData[category].assets[assetData.symbol] = assetEntry;
      }

      // Hedef kategoriyi de güncellenecekler listesine ekle
      affectedCategories.add(mainCategory || assetData.type);

      // 4. Tüm etkilenen kategorileri Firebase'e kaydet
      const updatePromises = Array.from(affectedCategories).map(catKey => 
          updateCategory(userEmail, catKey, updatedData[catKey], selectedPortfolioId)
      );
      
      await Promise.all(updatePromises);

      setPortfolioData(updatedData);
      console.log('Asset added and cleaned up successfully:', assetEntry);
    } catch (error) {
      console.error('Error adding asset:', error);
      throw error;
    }

    setModalState({
      ...modalState,
      showAddAsset: false,
      selectedParentPath: null
    });
  };

  const handleAddCategory = (parentPath) => {
    setModalState({
      ...modalState,
      showAddCategory: true,
      selectedParentPath: parentPath
    });
  };

  const handleCategorySubmit = async (categoryData) => {
    try {
      const updatedData = { ...portfolioData };
      
      if (modalState.selectedParentPath) {
        const mainCategory = modalState.selectedParentPath.split('|')[0];
        if (!updatedData[mainCategory]) {
          updatedData[mainCategory] = {
            name: mainCategory,
            allocation: 0,
            subCategories: {},
            assets: {}
          };
        }

        if (!updatedData[mainCategory].subCategories) {
          updatedData[mainCategory].subCategories = {};
        }

        updatedData[mainCategory].subCategories[categoryData.name] = {
          name: categoryData.name,
          allocation: categoryData.allocation || 0,
          assets: {}
        };

        await updateCategory(userEmail, mainCategory, updatedData[mainCategory], selectedPortfolioId);
      } else {
        updatedData[categoryData.name] = {
          name: categoryData.name,
          allocation: categoryData.allocation || 0,
          subCategories: {},
          assets: {}
        };

        await savePortfolioTargets(userEmail, updatedData, selectedPortfolioId);
      }
      
      setPortfolioData(updatedData);
    } catch (error) {
      console.error('Error adding category:', error);
    }
    
    setModalState({
      ...modalState,
      showAddCategory: false,
      selectedParentPath: null
    });
  };



  const handleDelete = async (path) => {
    if (!window.confirm('Bu öğeyi silmek istediğinizden emin misiniz?')) {
      return;
    }
  
    try {
      const updatedData = { ...portfolioData };
      const pathParts = path.split('|');
      const category = pathParts[0];
  
      if (pathParts.length === 1) {
        // En üst seviye kategori silme
        await deleteCategory(userEmail, category, selectedPortfolioId);
        delete updatedData[category];
      } else {
        // Alt öğe silme logic'i
        // pathParts: ['Category', 'subCategories', 'Sub', 'assets', 'AssetKey']
        let current = updatedData[category];
        
        // Parent'a kadar in
        for (let i = 1; i < pathParts.length - 1; i++) {
          if (!current[pathParts[i]]) {
             console.error("Delete path not found:", pathParts[i]);
             return;
          }
          current = current[pathParts[i]];
        }
        
        const itemKey = pathParts[pathParts.length - 1];
        if (current[itemKey]) {
           delete current[itemKey];
           await updateCategory(userEmail, category, updatedData[category], selectedPortfolioId);
        }
      }
      
      setPortfolioData(updatedData);
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };




  const handleAllocationChange = async (path, newValue) => {
    try {
      const updatedData = { ...portfolioData };
      const parts = path.split('|');
      const category = parts[0];
  
      // Kategori objesini derin kopyala (Immutability ve doğru referans değişimi için)
      if (updatedData[category]) {
          updatedData[category] = JSON.parse(JSON.stringify(updatedData[category]));
      }

      let current = updatedData;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]]) current[parts[i]] = {};
        current = current[parts[i]];
      }
      
      if (!current[parts[parts.length - 1]]) {
        current[parts[parts.length - 1]] = {};
      }
      current[parts[parts.length - 1]].allocation = parseFloat(newValue);
  
      // Optimistic Update: Önce UI güncelle
      setPortfolioData(updatedData);

      // Sonra DB güncelle
      await updateCategory(userEmail, category, updatedData[category], selectedPortfolioId);
    } catch (error) {
      console.error('Error updating allocation:', error);
      // Hata durumunda eski veriyi geri yüklemek için fetch yapılabilir
    }
  };

  const handleDistributeEqually = async (path) => {
    try {
      const updatedData = { ...portfolioData };
      const parts = path.split('|');
      const rootCategory = parts[0];

      // Kategori objesini derin kopyala
      if (updatedData[rootCategory]) {
          updatedData[rootCategory] = JSON.parse(JSON.stringify(updatedData[rootCategory]));
      }

      // Navigate to the target item using the new cloned object structure
      let current = updatedData;
      for (const part of parts) {
        if (!current[part]) return; // Path invalid
        current = current[part];
      }

      // Collect all children keys to distribute 100% among them
      const childrenLocations = [];
      
      if (current.subCategories) {
        Object.keys(current.subCategories).forEach(key => {
            childrenLocations.push({ collection: current.subCategories, key });
        });
      }
      
      if (current.assets) {
        Object.keys(current.assets).forEach(key => {
            childrenLocations.push({ collection: current.assets, key });
        });
      }

      const count = childrenLocations.length;
      if (count === 0) {
          // No children to distribute to
          return;
      }

      const equalShare = Math.floor((100 / count) * 100) / 100; // 2 decimal places floor
      let currentTotal = 0;

      childrenLocations.forEach((loc, index) => {
        if (index === count - 1) {
             // Give the remainder to the last item to sum exactly to 100
             loc.collection[loc.key].allocation = parseFloat((100 - currentTotal).toFixed(2));
        } else {
             loc.collection[loc.key].allocation = equalShare;
             currentTotal += equalShare;
        }
      });

      // Optimistic Update: Önce UI
      setPortfolioData(updatedData);

      // Save to Firebase
      await updateCategory(userEmail, rootCategory, updatedData[rootCategory], selectedPortfolioId);
      
    } catch (error) {
      console.error('Error distributing equally:', error);
    }
  };

  return {
    portfolioData,
    
    loading,
    modalState,
    handleAddAsset,
    handleAssetSubmit,
    handleAddCategory,
    handleCategorySubmit,
    handleDelete,
    handleAllocationChange,
    handleDistributeEqually,
    setModalState
  };
}